import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";
import { supabase } from "@/lib/supabase.js";

const SnapshotRow = z.object({
  id: z.string().uuid(),
  payload: z.unknown(),
  payload_hash: z.string(),
  published_at: z.string(),
  invalidated_at: z.string().nullable(),
});
export type SnapshotRow = z.infer<typeof SnapshotRow>;

export function useSnapshots(listingId: string | undefined) {
  return useQuery({
    queryKey: ["snapshots", listingId],
    enabled: !!listingId,
    queryFn: async (): Promise<SnapshotRow[]> => {
      const sb = supabase();
      const { data, error } = await sb
        .from("content_snapshots")
        .select("id, payload, payload_hash, published_at, invalidated_at")
        .eq("listing_id", listingId!)
        .order("published_at", { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return z.array(SnapshotRow).parse(data ?? []);
    },
  });
}

const RollbackResponse = z.object({
  snapshot_id: z.string().uuid(),
  rolled_back_at: z.string(),
});

export function useRollback(listingId: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: (snapshot_id: string) => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/listings/${listingId}/rollback/${snapshot_id}`, token, RollbackResponse, {
        method: "POST",
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["snapshots", listingId] });
      void qc.invalidateQueries({ queryKey: ["listing", listingId] });
    },
  });
}
